import { useCallback, useEffect, useRef, useState } from 'react';

// Shared state for live-synced slide games (board game, pictionary, mind map…).
// Every local change bumps `rev`; an incoming synced snapshot is adopted only
// when its `rev` is at least ours. That way the 500ms interactive-state poll
// can't roll us back to our own stale echo, while a concurrent write that won
// on the server (same rev) still converges both clients to the server copy.
const useSyncedState = (syncedState, onChange, createInitial) => {
  const valid = (s) => s && typeof s === 'object' && !Array.isArray(s);
  const [state, setState] = useState(() => (valid(syncedState) ? syncedState : createInitial()));
  const stateRef = useRef(state);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!valid(syncedState) || syncedState === stateRef.current) return;
    if ((syncedState.rev || 0) < (stateRef.current.rev || 0)) return;
    stateRef.current = syncedState;
    setState(syncedState);
  }, [syncedState]);

  const update = useCallback((updater) => {
    const prev = stateRef.current;
    const raw = typeof updater === 'function' ? updater(prev) : updater;
    if (!raw || raw === prev) return;
    const next = { ...raw, rev: (prev.rev || 0) + 1 };
    stateRef.current = next;
    setState(next);
    if (onChangeRef.current) onChangeRef.current(next);
  }, []);

  return [state, update];
};

export default useSyncedState;
